import {
    Anton,
    Archivo_Black,
    Bebas_Neue,
    Bricolage_Grotesque,
    Caveat,
    DM_Serif_Display,
    Figtree,
    Fraunces,
    Inter,
    JetBrains_Mono,
    Montserrat,
    Playfair_Display,
    Space_Grotesk
} from 'next/font/google'

// Every typeface used by the site and by the post designer is loaded here, once.
// The app faces (Figtree, Bricolage Grotesque, JetBrains Mono) are preloaded; the
// designer faces only download when a slide or preview actually uses them.

export const figtree = Figtree({ subsets: ['latin', 'latin-ext'], display: 'swap' })
export const bricolage = Bricolage_Grotesque({ subsets: ['latin', 'latin-ext'], display: 'swap' })
export const jetbrains = JetBrains_Mono({ subsets: ['latin', 'latin-ext'], display: 'swap' })

const inter = Inter({ subsets: ['latin', 'latin-ext'], display: 'swap', preload: false })
const archivoBlack = Archivo_Black({ weight: '400', subsets: ['latin', 'latin-ext'], display: 'swap', preload: false })
const anton = Anton({ weight: '400', subsets: ['latin', 'latin-ext'], display: 'swap', preload: false })
const bebas = Bebas_Neue({ weight: '400', subsets: ['latin', 'latin-ext'], display: 'swap', preload: false })
const spaceGrotesk = Space_Grotesk({ subsets: ['latin', 'latin-ext'], display: 'swap', preload: false })
const montserrat = Montserrat({ subsets: ['latin', 'latin-ext'], display: 'swap', preload: false })
const dmSerif = DM_Serif_Display({ weight: '400', style: ['normal', 'italic'], subsets: ['latin', 'latin-ext'], display: 'swap', preload: false })
const playfair = Playfair_Display({ subsets: ['latin', 'latin-ext'], display: 'swap', preload: false })
const fraunces = Fraunces({ subsets: ['latin', 'latin-ext'], display: 'swap', preload: false })
const caveat = Caveat({ subsets: ['latin', 'latin-ext'], display: 'swap', preload: false })

export type FontCategory = 'sans' | 'display' | 'serif' | 'hand' | 'mono'

export type FontDefinition = {
    id: string
    label: string
    family: string
    primary: string
    weights: Array<number>
    category: FontCategory
    widthFactor: number
}

const define = (id: string, label: string, family: string, weights: Array<number>, category: FontCategory, widthFactor: number): FontDefinition => ({
    id,
    label,
    family,
    primary: family.split(',')[0].trim(),
    weights,
    category,
    widthFactor
})

export const FONTS: Array<FontDefinition> = [
    define('inter', 'Inter', inter.style.fontFamily, [400, 500, 600, 700, 800, 900], 'sans', 0.56),
    define('figtree', 'Figtree', figtree.style.fontFamily, [400, 500, 600, 700, 800, 900], 'sans', 0.54),
    define('montserrat', 'Montserrat', montserrat.style.fontFamily, [400, 500, 600, 700, 800, 900], 'sans', 0.62),
    define('space-grotesk', 'Space Grotesk', spaceGrotesk.style.fontFamily, [400, 500, 600, 700], 'sans', 0.57),
    define('bricolage', 'Bricolage Grotesque', bricolage.style.fontFamily, [400, 500, 600, 700, 800], 'display', 0.55),
    define('archivo-black', 'Archivo Black', archivoBlack.style.fontFamily, [400], 'display', 0.68),
    define('anton', 'Anton', anton.style.fontFamily, [400], 'display', 0.44),
    define('bebas', 'Bebas Neue', bebas.style.fontFamily, [400], 'display', 0.4),
    define('dm-serif', 'DM Serif Display', dmSerif.style.fontFamily, [400], 'serif', 0.52),
    define('playfair', 'Playfair Display', playfair.style.fontFamily, [400, 500, 600, 700, 800, 900], 'serif', 0.55),
    define('fraunces', 'Fraunces', fraunces.style.fontFamily, [400, 500, 600, 700, 800, 900], 'serif', 0.56),
    define('caveat', 'Caveat', caveat.style.fontFamily, [400, 500, 600, 700], 'hand', 0.42),
    define('jetbrains', 'JetBrains Mono', jetbrains.style.fontFamily, [400, 500, 600, 700, 800], 'mono', 0.6)
]

const FONT_MAP = new Map(FONTS.map(font => [font.id, font]))

export const getFont = (id: string): FontDefinition => FONT_MAP.get(id) ?? FONT_MAP.get('inter') as FontDefinition

export { isFontId } from '@/lib/design/font-ids'

// The closest weight the face actually ships, so a static face is never faux-bolded.
export const nearestWeight = (id: string, weight: number): number => {
    const weights = getFont(id).weights

    return weights.reduce((best, current) => Math.abs(current - weight) < Math.abs(best - weight) ? current : best, weights[0])
}

export const fontCss = (id: string, size: number, weight: number, italic: boolean): string => {
    const font = getFont(id)

    return `${italic ? 'italic ' : ''}${nearestWeight(id, weight)} ${size}px ${font.family}`
}

const loaded = new Map<string, Promise<void>>()

// Makes sure a face is downloaded before it is drawn on a canvas. Browser only.
export const loadFont = (id: string, weight: number, italic = false): Promise<void> => {
    if (typeof document === 'undefined' || !document.fonts) return Promise.resolve()

    const font = getFont(id)
    const cacheKey = `${font.id}:${nearestWeight(id, weight)}:${italic ? 'i' : 'n'}`
    const existing = loaded.get(cacheKey)

    if (existing) return existing

    const promise = document.fonts
        .load(`${italic ? 'italic ' : ''}${nearestWeight(id, weight)} 48px ${font.primary}`)
        .then(() => undefined)
        .catch(() => undefined)

    loaded.set(cacheKey, promise)

    return promise
}
